import { ConfirmationDialog, useTranslate } from '@athena/ui'

import { useFormat } from '../../lib/format.js'

export interface TimedStartDialogProps {
  /** The practice about to be started, or null when the dialog is closed. */
  practice: { title: string; durationMinutes: number | null } | null
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

/**
 * What a student is told before a timed practice begins.
 *
 * The whole reason the timer does not start when the practice is opened: the
 * rules of an exam are worth stating once, in front of the student, before the
 * clock they cannot pause starts running. Four facts, in the order they
 * matter — how long, that it cannot be paused, that closing the browser does
 * not stop it, and what happens when it runs out.
 *
 * The existing confirmation dialog, not a new one: Radix owns the focus trap,
 * Escape, and returning focus to the trigger, and those are the parts that are
 * invisible when they are wrong. Its intent is `default`, so the confirming
 * button is the primary one, because starting a practice is the thing the
 * student came to do; "Not yet" still comes first, so focus starts there.
 */
export function TimedStartDialog({ practice, onOpenChange, onConfirm }: TimedStartDialogProps) {
  const t = useTranslate()
  const format = useFormat()

  const minutes = practice?.durationMinutes ?? null

  return (
    <ConfirmationDialog
      open={practice !== null}
      onOpenChange={onOpenChange}
      intent="default"
      title={t('learn.timed.dialogTitle')}
      // One string, because `ConfirmationDialog` puts it in the dialog's
      // accessible description and a description is one thing. The duration
      // leads, since it is the fact the student is deciding on.
      description={`${
        minutes === null ? '' : `${t('learn.timed.dialogDuration', [format.duration(minutes)])} `
      }${t('learn.timed.dialogRules')}`}
      confirmLabel={t('learn.timed.dialogStart')}
      cancelLabel={t('learn.timed.dialogCancel')}
      onConfirm={onConfirm}
    />
  )
}
